import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Role + permission management (ADR-0039). Phase 1 ships read-only
 * list + detail + the static permission registry. The full CRUD +
 * grant/revoke endpoints come in a follow-up; the schemas here keep
 * the write side optional so the API can grow into them.
 */

/**
 * Minimal role option for the user-create/edit form's Primary-role dropdown.
 * Served by GET /users/assignable-roles (ADMIN + FINANCE), so finance can pick a
 * role without access to the ADMIN-only /roles API. The ADMIN system role is
 * excluded for a FINANCE caller (finance cannot create admins).
 */
export const AssignableRoleSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  isSystem: z.boolean(),
  isActive: z.boolean(),
});
export type AssignableRole = z.infer<typeof AssignableRoleSchema>;
export const AssignableRolesResponseSchema = z.array(AssignableRoleSchema);
export type AssignableRolesResponse = z.infer<typeof AssignableRolesResponseSchema>;

export const RoleListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  parentRoleId: UuidSchema.nullable(),
  parentRoleCode: z.string().nullable(),
  isSystem: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  userCount: z.number().int(),
  permissionCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type RoleListItem = z.infer<typeof RoleListItemSchema>;

export const RoleDetailSchema = RoleListItemSchema.extend({
  permissions: z.array(z.string()),
  /// Effective permissions (own + parent chain), useful for the "what
  /// can this role do" preview.
  effectivePermissions: z.array(z.string()),
  updatedAt: IsoDateTimeSchema,
});
export type RoleDetail = z.infer<typeof RoleDetailSchema>;

export const RoleListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
});
export type RoleListQuery = z.infer<typeof RoleListQuerySchema>;

export const RoleListResponseSchema = paginatedResponseSchema(RoleListItemSchema);
export type RoleListResponse = z.infer<typeof RoleListResponseSchema>;

/**
 * The static registry returned by `GET /roles/registry`. Drives the
 * permission-matrix UI: modules group permissions; the UI renders a
 * collapsible section per group and one row per module with a
 * checkbox per action.
 *
 * ADR-0056: registry is now grouped by sidebar group. Source of truth
 * is `permissions-registry.ts` in @utazzo/shared.
 */
export const PermissionRegistryEntrySchema = z.object({
  moduleKey: z.string(),
  label: z.string(),
  description: z.string(),
  actions: z.array(z.string()),
  /// Sidebar group id this module belongs to. Matches
  /// `NavGroupConfig.id` on the admin side (operations, inventory,
  /// itinerary-masters, crm, global, website, system).
  groupKey: z.string(),
  /// Sidebar nav item this module gates (1:1 with
  /// `NavItemConfig.id`). Optional - some modules may not surface as
  /// a sidebar item.
  navItemId: z.string().nullable().optional(),
});
export type PermissionRegistryEntry = z.infer<typeof PermissionRegistryEntrySchema>;

export const PermissionRegistryGroupSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string(),
});
export type PermissionRegistryGroup = z.infer<typeof PermissionRegistryGroupSchema>;

export const PermissionRegistryResponseSchema = z.object({
  /// One entry per sidebar group in display order. The matrix renders
  /// these as collapsible sections in order.
  groups: z.array(PermissionRegistryGroupSchema),
  /// Flat list of all modules. Each module carries its `groupKey`
  /// so the UI can bin them under the right group header.
  modules: z.array(PermissionRegistryEntrySchema),
});
export type PermissionRegistryResponse = z.infer<typeof PermissionRegistryResponseSchema>;

/**
 * Role create / update / permission-set payloads (ADR-0039 Phase 2).
 *
 * `code` is lowercase + snake-case (see existing seed; matches the
 * `UserRole` enum on the user side). `parentRoleId` may be null for a
 * root role. Setting `parentRoleId` to a descendant is rejected
 * server-side.
 */
const RoleCodeSchema = z
  .string()
  .trim()
  .min(2)
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters, digits, underscores only');

export const CreateRoleRequestSchema = z.object({
  code: RoleCodeSchema,
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).nullable().optional(),
  parentRoleId: UuidSchema.nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type CreateRoleRequest = z.infer<typeof CreateRoleRequestSchema>;

export const UpdateRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  parentRoleId: UuidSchema.nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequestSchema>;

/**
 * Idempotent set-the-full-list semantics: the server replaces the
 * role's permissions with exactly this list (computes the grant /
 * revoke diff and writes one audit row per change). Empty list = revoke
 * everything.
 */
export const SetRolePermissionsRequestSchema = z.object({
  permissions: z.array(z.string().min(1)).max(500),
});
export type SetRolePermissionsRequest = z.infer<typeof SetRolePermissionsRequestSchema>;
